import store from 'store2'
import { useState, useEffect, useCallback, useRef } from 'react'
import useStorefront from './use-storefront'
import { BENELUX_FREE, getProductLink } from 'components/flyouts/Cart'
import { cvgTrack, parseLineItem } from 'components/analytics/Converge'

const GIFT_PRODUCT_ID = 'gid://shopify/Product/7229558554810'
const GIFT2_PRODUCT_ID = 'gid://shopify/Product/7272968290490'
export const GIFT_1_THRESHOLD = 95
export const GIFT_2_THRESHOLD = 150

export type LineItem = {
	id: string
	title: string
	variant: {
		id: string
		title: string
		image: {
			originalSrc: string
		}
		product: {
			id: string
			handle: string
			productType: string
			title: string
		}
		selectedOptions: {
			name: string
			value: string
		}[]
		priceV2: {
			amount: string
		}
		compareAtPrice: {
			amount: string
		}
	}
	quantity: number
}

export type Cart = {
	id: string
	checkoutUrl: string
	count: number
	note?: string
	lineItems: LineItem[]
	lineItemsSubtotalPrice: {
		amount: string
	}
	freeGift: {
		gift?: LineItem
		percent: number
		remaining: number
	}
	freeGift2: {
		gift?: LineItem
		percent: number
		remaining: number
	}
}

const checkoutQuery = /* GraphQL */ `
	id
	checkoutUrl
	cost {
		totalAmount {
			amount
		}
		subtotalAmount {
			amount
		}
		checkoutChargeAmount {
			amount
		}
	}
	note
	totalQuantity
	lines (first: 50) {
		nodes {
			id
			merchandise {
				... on ProductVariant {
					id
					title
					image {
						originalSrc
					}
					product {
						id
						handle
						title
						productType
					}
					selectedOptions {
						name
						value
					}
					priceV2 {
						amount
					}
					compareAtPrice {
						amount
					}
				}
			}
			quantity
		}
	}
`

const percent = ( fraction: number ) => (
	Math.max( 0, Math.min( 100, fraction * 100 ) )
)

const toLineItem = line => ({
	id: line.id,
	title: line.merchandise.product.title,
	variant: line.merchandise,
	quantity: line.quantity,
})

const processCart = ( cart ): Cart => {
	const lineItems = cart.lines.nodes.map( toLineItem )
	const subtotalWithoutGiftcards = getSubtotalOfItemsEligibleForGift( lineItems )
	return {
		id: cart.id,
		checkoutUrl: cart.checkoutUrl,
		note: cart.note,
		count: cart.totalQuantity,
		lineItems,
		lineItemsSubtotalPrice: cart.cost.subtotalAmount,
		freeGift: {
			gift: lineItems.find(
				({ variant }) => gifts.first.map( ( g ) => g.id ).indexOf( variant.id ) > -1
			),
			percent: percent(
				( subtotalWithoutGiftcards - BENELUX_FREE ) /
					( GIFT_1_THRESHOLD - BENELUX_FREE )
			),
			remaining:
				gifts.first.filter( ( g ) => g.availableForSale ).length > 0
					? Math.max( 0, GIFT_1_THRESHOLD - subtotalWithoutGiftcards )
					: 0,
		},
		freeGift2: {
			gift: lineItems.find(
				({ variant }) => gifts.second.map( ( g ) => g.id ).indexOf( variant.id ) > -1
			),
			percent: percent(
				( subtotalWithoutGiftcards - GIFT_1_THRESHOLD ) /
					( GIFT_2_THRESHOLD - GIFT_1_THRESHOLD )
			),
			remaining:
				gifts.second.filter( ( g ) => g.availableForSale ).length > 1
					? Math.max( 0, GIFT_2_THRESHOLD - subtotalWithoutGiftcards )
					: 0,
		},
	}}

// Get current gift options
type GiftVariant = {
	id: string
	title: string
	availableForSale: boolean
	compareAtPrice: {
		amount: string
	}
	image: {
		originalSrc: string
	}
}
const giftQuery = `
	query ($productId: ID!) @inContext(country: BE) {
		product(id: $productId) {
			variants (first: 20) {
				nodes {
					id
					title
					availableForSale
					compareAtPrice {
						amount
					}
					image {
						originalSrc
					}
				}
			}
		}
	}
`
export const gifts: { first: GiftVariant[]; second: GiftVariant[] } = {
	first: [],
	second: [],
}
const client = useStorefront()
client
	.request( giftQuery, { productId: GIFT_PRODUCT_ID })
	.then( ({ product: { variants } }) => {
		gifts.first = variants.nodes
	})
client
	.request( giftQuery, { productId: GIFT2_PRODUCT_ID })
	.then( ({ product: { variants } }) => {
		gifts.second = variants.nodes
	})

export const isEligibleForGift = ( lineItem: LineItem ) => (
	lineItem.variant.product.productType !== 'Gift Cards'
)

export const getSubtotalOfItemsEligibleForGift = ( lineItems: Cart['lineItems'] ) => (
	lineItems
		?.filter( isEligibleForGift )
		.reduce( ( a, v ) => (
			a + parseFloat( v.variant.priceV2.amount ) * v.quantity
		), 0 ) ?? 0
)

const checkFreeGift = (
	cart: Cart,
	gift: Cart['freeGift'],
	bar: number,
	base: number
) => {
	// Recalculate gift eligibility
	const subtotal = getSubtotalOfItemsEligibleForGift( cart.lineItems )
	gift.remaining = Math.max( 0, bar - subtotal )
	gift.percent = percent( ( subtotal - base ) / ( bar - base ) )

	const entitled = gift.percent === 100

	if ( entitled && !gift?.gift ) return 'add'
	if ( !entitled && gift?.gift ) return 'remove'
}

const applyFreeGift = ( cart: Cart ) => {
	const gift1 = checkFreeGift(
		cart,
		cart.freeGift,
		GIFT_1_THRESHOLD,
		BENELUX_FREE
	)
	const gift2 = checkFreeGift(
		cart,
		cart.freeGift2,
		GIFT_2_THRESHOLD,
		GIFT_1_THRESHOLD
	)

	// Prevent showing progress bar if there are no gifts available
	if ( !gifts.first.some( g => g.availableForSale ) ) cart.freeGift.remaining = 0
	if ( !gifts.second.some( g => g.availableForSale ) ) cart.freeGift2.remaining = 0

	const add = []
	if ( gift1 === 'add' && gifts.first[ 0 ] ) {
		add.push({ merchandiseId: gifts.first[ 0 ].id })
	}
	if ( gift2 === 'add' && gifts.second[ 0 ] ) {
		add.push({ merchandiseId: gifts.second[ 0 ].id })
	}

	const update = []
	if ( gift1 === 'remove' ) {
		update.push({ id: cart.freeGift.gift.id, quantity: 0 })
	}
	if ( gift2 === 'remove' ) {
		update.push({ id: cart.freeGift2.gift.id, quantity: 0 })
	}

	return { update, add }
}

export default function useCart() {
	const client = useStorefront()
	const [ cart, realSetCart ] = useState<Cart>( () => {
		const stored = store( 'cart' )

		// Ignore invalid data or old carts linked to the checkout API
		if ( /^gid:\/\/shopify\/Cart\/.+/.test( stored?.id ) ) {
			return stored
		}
	})
	// eslint-disable-next-line max-len
	// const setCart = newCart => !equal( newCart, cart ) && realSetCart( newCart )
	const setCart = realSetCart
	const cartRef = useRef<Cart>( cart )
	cartRef.current = cart

	const create = async () => {
		const { cartCreate: { cart } } = await client.request( `
			mutation cartCreate ($input: CartInput!) @inContext(country: BE) {
				cartCreate (input: $input) {
					cart {
						${checkoutQuery}
					}
				}
			}
		`, {
			input: {},
		})

		setCart( processCart( cart ) )
	}

	const refresh = async () => {
		if ( !cart ) {
			create()
		} else {
			const { cart: result } = await client.request( `query @inContext(country: BE) {
				cart(id: "${cart.id}") {
					${checkoutQuery}
				}
			}` )

			if ( !result ) {
				create()
			} else {
				setCart( processCart( result ) )
			}
		}
	}

	useEffect( () => { refresh() }, [] )
	useEffect( () => { store( 'cart', cart ) }, [ cart ] )

	/** Only meant for passing gift preferences, elegibility is checked elsewhere */
	const gift = useCallback( async ( which: 'first'|'second', variantId: string ) => {
		const cart = cartRef.current, add = [], updates = []

		if (
			which === 'first' &&
			cart.freeGift.gift &&
			cart.freeGift.gift.variant.id !== variantId
		) {
			updates.push({ id: cart.freeGift.gift.id, quantity: 0 })
			add.push({ merchandiseId: variantId })
		}

		if (
			which === 'second' &&
			cart.freeGift2.gift &&
			cart.freeGift2.gift.variant.id !== variantId
		) {
			updates.push({ id: cart.freeGift2.gift.id, quantity: 0 })
			add.push({ merchandiseId: variantId })
		}

		return client.request( `
			mutation updateGifts(
				$cartId: ID!,
				$updates: [CartLineUpdateInput!]!
				${add.length ? ', $add: [CartLineInput!]!' : ''}
			) {
				${add.length ? `
					cartLinesAdd(
						cartId: $cartId,
						lines: $add
					) {
						cart {
							note
						}
					}
				` : ''}
				cartLinesUpdate(
					cartId: $cartId,
					lines: $updates
				) {
					cart {
						${checkoutQuery}
					}
					userErrors {
						code
						field
						message
					}
				}
			}
		`, { cartId: cart.id, updates, add }).then(
			({ cartLinesUpdate: { cart, userErrors } }) => {
				userErrors?.forEach( ({ code, field, message }) => {
					console.error( `${code} ${field} ${message}` )
				})

				if ( cart ) setCart( processCart( cart ) )
			})
	}, [] )

	// eslint-disable-next-line max-len
	const add = useCallback( async ( variant: LineItem['variant'], quantity: number = 1 ) => {
		const cart: Cart = {
			...cartRef.current,
			lineItems: [
				...cartRef.current.lineItems,
				{ id: 'adding', title: variant.title, variant, quantity },
			],
			lineItemsSubtotalPrice: {
				amount: (
					+cartRef.current.lineItemsSubtotalPrice.amount +
					+variant.priceV2.amount * quantity
				).toString(),
			},
			count: cartRef.current.count + quantity,
		}

		const { add } = applyFreeGift( cart )
		const items = [{ merchandiseId: variant.id, quantity }, ...add ]

		// Optimistic update
		setCart( cart )

		const { cartLinesAdd: { cart: checkout } } = await client.request( `
			mutation addLineItem($cartId: ID!, $items: [CartLineInput!]!) @inContext(country: BE) {
				cartLinesAdd(
					cartId: $cartId,
					lines: $items
				) {
					cart {
						${checkoutQuery}
					}
				}
			}
		`, { cartId: cart.id, items })

		// Update cart with the final (definitely correct) count
		const newCart = processCart( checkout )
		setCart( newCart )

		// Track add to cart in Klaviyo
		window._learnq = window._learnq || []
		if ( quantity > 0 ) {
			window._learnq.push( [ 'track', 'Added to Cart', {
				$value: newCart.lineItemsSubtotalPrice.amount,
				ItemNames: newCart.lineItems.map( i => i.title ),
				CheckoutURL: newCart.checkoutUrl,
				Items: newCart.lineItems.map( item => ({
					'ProductID': item.id,
					'SKU': item.variant.id,
					'ProductName': item.title,
					'Quantity': item.quantity,
					'ItemPrice': item.variant.priceV2.amount,
					'RowTotal': +item.variant.priceV2.amount * item.quantity,
					'ProductURL': `https://ray.care/${getProductLink({ product: item })}`,
					'ImageURL': item.variant.image.originalSrc,
				}) ),
			}] )
		}

		// Track add to cart in GTM
		if ( window.dataLayer ) {
			window.dataLayer.push({ ecommerce: null })
			newCart.lineItems.filter( l => l.variant.id === variant.id ).forEach( item => {
				if ( !item.variant.product.id ) return
				window.dataLayer.push({
					'event': 'addToCart',
					'ecommerce': {
						'currencyCode': 'EUR',
						'add': {
							'products': [{
								'name': item.title,
								'id': item.variant.product.id.substring(
									item.variant.product.id.lastIndexOf( '/' ) + 1
								),
								'price': item.variant.priceV2.amount,
								'brand': 'Ray',
								'variant': item.variant.selectedOptions.map( o => o.value ).join( ' / ' ),
								'quantity': quantity,
							}],
						},
					},
				})
			})
		}
		const cartId = newCart.id.split( '/' ).pop()
		cvgTrack({
			eventName: 'Added To Cart',
			properties: parseLineItem({
				...newCart.lineItems.filter( l => l.variant.id === variant.id )[ 0 ],
				quantity,
			}),
			aliases: cartId ? [ `urn:shopify:cart_token:${cartId}` ] : [],
		})

		return checkout
	}, [ cart?.id ] )

	const note = useCallback( async ( note: string ) => {
		await client.request( `
			mutation checkoutAttributesUpdateV2 ($checkoutId: ID!, $input: CheckoutAttributesUpdateV2Input!) @inContext(country: BE) {
				checkoutAttributesUpdateV2(checkoutId: $checkoutId, input: $input) {
					checkout {
						id
					}
					checkoutUserErrors {
						code
						field
						message
					}
				}
			}
		`, { checkoutId: cart.id, input: { note } })
	}, [ cart?.id ] )

	const update = useCallback( ( lineItemId: string, quantity: number ) => {
		const item = cartRef.current.lineItems.find( ({ id }) => id === lineItemId )
		const cart: Cart = {
			...cartRef.current,
			lineItems: cartRef.current.lineItems.map( ( lineItem ) =>
				lineItem.id === lineItemId
					? { ...lineItem, quantity }
					: lineItem
			),
			lineItemsSubtotalPrice: {
				amount: (
					+cartRef.current.lineItemsSubtotalPrice.amount +
							( quantity - item.quantity ) * +item.variant.priceV2.amount
				).toString(),
			},
			count: cartRef.current.count + ( quantity - item.quantity ),
		}

		const { update, add } = applyFreeGift( cart )
		const updates = [{ id: lineItemId, quantity }, ...update ]

		// Optimistic update
		setCart( cart )

		const cartId = cart.id.split( '/' ).pop()
		cvgTrack({
			eventName: (
				quantity < item.quantity ? 'Removed From Cart' : 'Added To Cart'
			),
			properties: parseLineItem({
				...item,
				quantity: Math.abs( quantity - item.quantity ),
			}),
			aliases: cartId ? [ `urn:shopify:cart_token:${cartId}` ] : [],
		})

		return client.request( `
			mutation updateLineItem(
				$cartId: ID!,
				$updates: [CartLineUpdateInput!]!
				${add.length ? ', $add: [CartLineInput!]!' : ''}
			) @inContext(country: BE) {
				${add.length ? `
					cartLinesAdd(
						cartId: $cartId,
						lines: $add
					) {
						cart {
							note
						}
					}
				` : ''}
				cartLinesUpdate(
					cartId: $cartId,
					lines: $updates
				) {
					cart {
						${checkoutQuery}
					}
					userErrors {
						code
						field
						message
					}
				}
			}
		`, { cartId: cart.id, updates, add }).then(
			({ cartLinesUpdate: { cart, userErrors } }) => {
				userErrors?.forEach( ({ code, field, message }) => {
					console.error( `${code} ${field} ${message}` )
				})

				if ( cart ) setCart( processCart( cart ) )
			})
	}, [ cart?.id ] )

	return { cart, add, refresh, note, update, gift }
}
